import React, { useState, useCallback, useEffect, use } from "react";
import { useParams } from "react-router-dom";
import UserPageTemplate from "../templates/UserPageTemplate";
import { User } from "../types/User";
import UserModal from "../molecules/UserModal";
import MainLayout from "../../Layouts/MainLayout";
import axios from "../../../config/axios";
import AccessControl from "../molecules/AccessControl";
import AccessDenied from "../atoms/AccessDenied";
import Spinner from "../atoms/Spinner";
import useAccessCheckLoading from "../../../utils/useAccessCheckLoading";
import { service } from "../../../services/AxiosServices";
import { toast } from "react-toastify";
import SectionHeader from "../molecules/SectionHeader";
type RoleOption = {
  label: string;
  value: string;
  id?: string;
};
// Handles user role management by allowing selection of users, assigning roles, and saving the changes,
//  with modal support for adding new users

const UserPage: React.FC = () => {
  const dummyRoles = {
    // configuration: [
    //   { label: "Crew Admin", value: "Crew Admin", id: 1 },
    //   { label: "Crew Trainer", value: "Crew Trainer", id: 2 },
    //   { label: "Fine Tuner", value: "Fine Tuner", id: 3 },
    //   { label: "Output", value: "Output", id: 4 },
    //   { label: "Space Admin", value: "Space Admin", id: 5 },
    //   { label: "Space Knowledge Base", value: "Space Knowledge Base", id: 6 },
    //   { label: "Space Fine-tuning", value: "Space Fine-tuning", id: 7 },
    //   { label: "Training", value: "Training", id: 8 },
    // ],
    // crew: [
    //   { label: "Crew Admin", value: "Crew Admin", id: 1 },
    //   { label: "Crew Trainer", value: "Crew Trainer", id: 2 },
    //   { label: "Fine Tuner", value: "Fine Tuner", id: 3 },
    //   { label: "Output", value: "Output", id: 4 },
    // ],
    // space: [
    //   { label: "Knowledge holder", value: "Knowledge holder", id: 5 },
    //   { label: "Knowledge consumer", value: "Knowledge consumer", id: 6 },
    //   { label: "Finetuner", value: "Finetuner", id: 7 },
    // ],
    training: [{ label: "Training", value: "Training", id: 8 }],
  };

  const { pageContext } = useParams<{ pageContext: string }>();
  const [users, setUsers] = useState<User[]>([]);
  const [allUsers, setAllUsers] = useState<User[]>([]);
  const [roleOptions, setRoleOptions] = useState(
    dummyRoles[pageContext as keyof typeof dummyRoles] || []
  );
  const [roleChanges, setRoleChanges] = useState<Record<string, string>>({});
  const [selectedUsername, setSelectedUsername] = useState("");
  const [loading, setLoading] = useState<boolean>(false);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [clientErrors, setClientErrors] = useState<{
    selectedUsername?: string;
  }>({});
  const {id} = useParams()
  const accessCheckLoading = useAccessCheckLoading()

  const validations = () => {
    let errors: Record<string, any> = {};

    if (users.length === 0) {
      errors.selectedUsername = "At least one user needs to be selected";
    }

    users.forEach((user) => {
      if (!roleChanges[user.username] && !user.role) {
        errors[user.username] = `Role is required for ${user.username}`;
      }
    });

    return errors;
  };

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await service<{ response: any[] }>(
          "/user-list",
          "post"
        );

        const apiUsers = response.data.response.map((user: any) => ({
          id: user.id,
          username: `${user.first_name || ""}_${user.last_name || ""}`,
          firstName: user.first_name || "",
          lastName: user.last_name || "",
          email: user.email || "",
          role: "",
        }));

        setAllUsers(apiUsers);
      } catch (err) {
        console.error("Error fetching users:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchRoles = async () => {
      // if (pageContext !== "configuration") return;

      try {
        setLoading(true);
        const response = await service<{ response: any[] }>(
          "/roles",
          "post",
          {}
        );

        const apiRoles = response.data.response
          .map((role: any) => ({
            label: role.name,
            value: role.name,
            id: Number(role.id),
          }))
          .sort((a, b) => a.label.localeCompare(b.label));

        if (apiRoles.length > 0) {
          setRoleOptions(apiRoles);
        }
      } catch (err) {
        console.log("Error fetching roles:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchRoles();
  }, [pageContext]);


  useEffect(() => {
    const fetchEntityUsers = async () => {

    
      try {
        setLoading(true);
        const formData = new FormData()
        formData.append('entity_id',id?.toString() || '');
        formData.append('entity_type_id',( pageContext === "crew" ? 1 : 2).toString());
        const response = await service<any>(
          "api/get-user-details-by-entity",
          "post",
          formData,
          "multipart/form-data"
        );

        if (response.data && response.data.users) {
          
          // Transform API response to User objects with roles
          const linkedUsers = response.data.users.map((user: any) => {
            // Find this user in allUsers to get firstName and lastName if available
            const existingUser = allUsers.find(u => u.id === user.user_id);
            
            // Map the roles to string format
            const userRoles = (user.roles || []).map((r: any) => r.role_name).join(", ");
            
            // Create user role mapping for the roleChanges state if roles exist
            if (userRoles) {
              setRoleChanges(prev => ({
                ...prev, 
                [`${user.username}`]: userRoles
              }));
            }
            
            return {
              id: user.user_id,
              username: user.username,
              firstName: existingUser?.firstName || "",
              lastName: existingUser?.lastName || "",
              email: user.email || "",
              role: userRoles,
              roles: user.roles || []
            };
          });

          // Add these users to the table
          setUsers(prevUsers => {
            // Filter out duplicates
            const newUsers = linkedUsers.filter(
              (newUser: User) => !prevUsers.some(existingUser => existingUser.id === newUser.id)
            );
            return [...prevUsers, ...newUsers];
          });
        
        }
      } catch (err) {
        console.error("Error fetching entity users:", err);
      } finally {
        setLoading(false);
      }
    };

    if (allUsers.length > 0) {
      fetchEntityUsers();
    }
  }, [allUsers,id]);


  const dropdownUsers = [
    "Select",
    ...allUsers
      .filter(
        (user) =>
          user.username.trim() !== "" &&
          !users.some((tableUser) => tableUser.id === user.id)
      )
      .sort((a, b) => a.username.localeCompare(b.username))
      .map((user) => user.username),
  ];

  const handleUserSelect = (username: string) => {
    if (username === "Select") return;

    const user = allUsers.find((user) => user.username === username);
    if (user) {
      setUsers((prevUsers) => [...prevUsers, user]);
      setSelectedUsername("Select"); 

      setClientErrors((prevErrors) => {
        const newErrors = { ...prevErrors };
        if (Object.keys(newErrors).length > 0) {
          delete newErrors.selectedUsername;
        }
        return newErrors;
      });
    }
  };

  const handleRoleChange = useCallback((username: string, roles: string) => {
    setRoleChanges((prev) => ({ ...prev, [username]: roles }));
  }, []);
  const handleSaveAll = async () => {
    const errors = validations();
    if (Object.keys(errors).length > 0) {
      setClientErrors(errors);
      return;
    }

    setClientErrors({});

    const payload = {
      users: users.map((user) => ({
        user_id: user.id,
        role_ids: roleChanges[user.username]
          ? roleOptions
              .filter((role) => roleChanges[user.username].includes(role.value))
              .map((role) => role.id) 
          : [],
      })),
      entity_id:id,
      entity_type_id: pageContext === "crew" ? 1 :2
    };
 

    try {
      const response = await service("api/get-entity-type-id-user-details", "post", payload);
    toast.success('Roles added succesfully',{autoClose:200})
      console.log("Save successful", response.data);
    } catch (err) {
      console.error("Error saving user roles:", err);
    }
  };

  const handleAddUser = () => setShowAddUserModal(true);
  const handleCloseModal = () => setShowAddUserModal(false);
  const handleSaveNewUser = (newUser: User) => {
    setAllUsers((prev) => {
      if (!prev.some((user) => user.username === newUser.username)) {
        return [...prev, newUser];
      }
      return prev;
    });
  };
  const clientErrorsArray = Object.entries(clientErrors).reduce(
    (acc: string[], [field, error]) => {
      if (error) {
        acc.push(`${error}`);
      }
      return acc;
    },
    []
  );

  useEffect(() => {
    console.log(roleChanges);
  }, [roleChanges]);
  return (
    <>
      <MainLayout>
      <AccessControl
        requiredPermissions={[
          {
            permission: pageContext === "crew" ? "crews_users":"manage_space_users",
            types: ["read"],
          },
        ]}
        renderNoAccess={ accessCheckLoading ? <Spinner/> :<AccessDenied />}
      >
      <SectionHeader back={true} title="Manage users" onAdd={handleAddUser} action='Create user' />
        <UserPageTemplate
          users={users}
          dropdownUsers={dropdownUsers}
          selectedUsername={selectedUsername}
          roleOptions={roleOptions}
          onUserSelect={handleUserSelect}
          onRoleChange={handleRoleChange}
          onSaveAll={handleSaveAll}
    
          clientErrors={clientErrorsArray}
        />
        <UserModal
          show={showAddUserModal}
          onClose={handleCloseModal}
          onSave={handleSaveNewUser}
        />
        </AccessControl>
      </MainLayout>
      
    </>
  );
};

export default UserPage;
